import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// ---------------------------------------------------------------------------
// Templates (admin management — list/create/sync/delete). The Plans bulk-send
// modal has its own narrower read-only `useWhatsappTemplates` in usePlanSummary.js
// (APPROVED-only, no mutations) — kept separate since that one's only concern is
// "what can I send right now", not managing the catalog.
// ---------------------------------------------------------------------------

const fetchAllTemplates = async (status) => {
  const { data } = await axios.get(`/whatsapp-templates${status ? `?status=${status}` : ""}`);
  return data;
};

// Registered WhatsApp numbers (each tied to its own WABA) — used to let the template
// builder pick which number/WABA a new template belongs to, and to label existing
// templates with their number in the list, now that there's more than one.
const fetchWhatsappNumbers = async () => {
  const { data } = await axios.get("/whatsapp-admin/tokens");
  return data;
};

export const useWhatsappNumbers = () =>
  useQuery({
    queryKey: ["whatsapp-numbers"],
    queryFn: fetchWhatsappNumbers,
    staleTime: 5 * 60 * 1000,
  });

// Claude follow-up settings for one number (admin only) — toggle, extra prompt, daily cap.
// Backend: PATCH /whatsapp-admin/tokens/:phoneNumberId/ai.
const updateAiSettings = async ({ phoneNumberId, ...payload }) => {
  const { data } = await axios.patch(`/whatsapp-admin/tokens/${phoneNumberId}/ai`, payload);
  return data;
};

export const useUpdateWhatsappAiSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAiSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-numbers"] }),
  });
};

// Per-thread "Pause AI" switch in the Inbox.
export const useSetWhatsappAiPaused = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, paused }) => {
      const { data } = await axios.post(`/whatsapp-admin/conversations/${encodeURIComponent(conversationId)}/ai-pause`, { paused });
      return data;
    },
    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-thread", conversationId] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-inbox"] });
    },
  });
};

// On-demand trigger for the same phone-quality/tier + template-quality backstop poll that
// runs on a schedule server-side (every 3h, independent of webhooks) — lets an admin force
// a refresh right after a change on Meta's side instead of waiting for the next tick.
const syncQuality = async () => {
  const { data } = await axios.post("/whatsapp-admin/tokens/sync-quality");
  return data;
};

export const useSyncWhatsappQuality = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncQuality,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-numbers"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] });
    },
  });
};

const createTemplate = async (payload) => {
  const { data } = await axios.post("/whatsapp-templates", payload);
  return data;
};

const syncTemplateStatus = async (id) => {
  const { data } = await axios.post(`/whatsapp-templates/${id}/sync-status`);
  return data;
};

const syncTemplatesFromMeta = async () => {
  const { data } = await axios.post("/whatsapp-templates/sync");
  return data;
};

const deleteTemplate = async (id) => {
  const { data } = await axios.delete(`/whatsapp-templates/${id}`);
  return data;
};

const updateTemplate = async ({ id, ...payload }) => {
  const { data } = await axios.patch(`/whatsapp-templates/${id}`, payload);
  return data;
};

const uploadTemplateMedia = async ({ file, wabaId }) => {
  const form = new FormData();
  form.append("file", file);
  if (wabaId) form.append("wabaId", wabaId);
  const { data } = await axios.post("/whatsapp-templates/upload-media", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const useAllWhatsappTemplates = (status) =>
  useQuery({
    queryKey: ["whatsapp-templates-admin", status || "all"],
    queryFn: () => fetchAllTemplates(status),
    staleTime: 30 * 1000,
  });

export const useCreateWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] }),
  });
};

export const useSyncWhatsappTemplateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncTemplateStatus,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] }),
  });
};

export const useSyncWhatsappTemplatesFromMeta = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncTemplatesFromMeta,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] }),
  });
};

export const useDeleteWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] }),
  });
};

export const useUploadTemplateMedia = () => useMutation({ mutationFn: uploadTemplateMedia });

export const useUpdateWhatsappTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-templates-admin"] }),
  });
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

const fetchCampaigns = async () => {
  const { data } = await axios.get("/whatsapp-campaigns");
  return data;
};

const createCampaign = async (payload) => {
  const { data } = await axios.post("/whatsapp-campaigns", payload);
  return data;
};

const runCampaign = async (campaignId) => {
  const { data } = await axios.post(`/whatsapp-campaigns/${campaignId}/run`);
  return data;
};

const deleteCampaign = async (campaignId) => {
  const { data } = await axios.delete(`/whatsapp-campaigns/${campaignId}`);
  return data;
};

export const useWhatsappCampaigns = () =>
  useQuery({
    queryKey: ["whatsapp-campaigns"],
    queryFn: fetchCampaigns,
    staleTime: 30 * 1000,
  });

export const useCreateWhatsappCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-campaigns"] }),
  });
};

export const useRunWhatsappCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-campaigns"] }),
  });
};

export const useDeleteWhatsappCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-campaigns"] }),
  });
};

// ---------------------------------------------------------------------------
// Payment Queue -> "Send link on WhatsApp" (campaign details page). The server works out,
// per selected queue row, whether the creator or the vendor gets the message and which
// link they get — `check` previews that (and why a row can't be sent), `send` does it and
// records a WhatsApp Campaign. See routes/whatsappCampaigns.js (/payment-links).
// ---------------------------------------------------------------------------

const checkPaymentLinkRecipients = async (payload) => {
  const { data } = await axios.post("/whatsapp-campaigns/payment-links/check", payload);
  return data;
};

const sendPaymentLinks = async (payload) => {
  const { data } = await axios.post("/whatsapp-campaigns/payment-links", payload);
  return data;
};

export const useCheckPaymentLinkRecipients = () =>
  useMutation({ mutationFn: checkPaymentLinkRecipients });

export const useSendPaymentLinksOnWhatsapp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendPaymentLinks,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-campaigns"] });
      // The per-row WhatsApp button appears as soon as a send lands.
      queryClient.invalidateQueries({ queryKey: ["payment-link-chats"] });
    },
  });
};

// Which Payment Queue rows of a payment campaign already have a WhatsApp chat because a
// payment link was sent to them (and the Inbox conversation to open) — rows not listed get
// no WhatsApp button.
const fetchPaymentLinkChats = async (campaignId) => {
  const { data } = await axios.post("/whatsapp-campaigns/payment-links/chats", { campaignId });
  return data;
};

export const usePaymentLinkChats = (campaignId, enabled = true) =>
  useQuery({
    queryKey: ["payment-link-chats", campaignId],
    queryFn: () => fetchPaymentLinkChats(campaignId),
    enabled: Boolean(campaignId) && enabled,
    staleTime: 30 * 1000,
  });

// "View WhatsApp Chats" screen for a payment campaign (components/paymentqueues/
// PaymentLinkWhatsAppPage.jsx) — the equivalent of the Plans screen's per-plan conversations,
// scoped on the server to chats a payment link was actually sent on for this campaign.
export const usePaymentLinkConversations = (campaignId) =>
  useQuery({
    queryKey: ["payment-link-conversations", campaignId],
    queryFn: async () => {
      const { data } = await axios.get("/whatsapp-campaigns/payment-links/conversations", { params: { campaignId } });
      return data;
    },
    enabled: Boolean(campaignId),
    staleTime: 15 * 1000,
  });

export const usePaymentLinkThread = (campaignId, conversationId) =>
  useQuery({
    queryKey: ["payment-link-thread", campaignId, conversationId],
    queryFn: async () => {
      const { data } = await axios.get(
        `/whatsapp-campaigns/payment-links/conversations/${encodeURIComponent(conversationId)}`,
        { params: { campaignId } }
      );
      return data;
    },
    enabled: Boolean(campaignId) && Boolean(conversationId),
    staleTime: 10 * 1000,
  });

export const useSendPaymentLinkMessage = (campaignId) =>
  useMutation({
    mutationFn: async ({ conversationId, text }) => {
      const { data } = await axios.post(
        `/whatsapp-campaigns/payment-links/conversations/${encodeURIComponent(conversationId)}/send`,
        { campaignId, text }
      );
      return data;
    },
  });

// ---------------------------------------------------------------------------
// Inbox — every conversation across every number, newest activity first. Distinct from
// Payment History's per-creator WhatsApp tab (that one starts from a known creatorId);
// this is the admin-facing "what needs a reply right now" view, and also surfaces anyone
// who's messaged us without being a known RapidInfluencer at all.
// ---------------------------------------------------------------------------

const fetchWhatsappInbox = async ({ page = 1, phoneNumberId } = {}) => {
  const params = new URLSearchParams({ page });
  if (phoneNumberId) params.set("phoneNumberId", phoneNumberId);
  const { data } = await axios.get(`/whatsapp-admin/conversations?${params.toString()}`);
  return data;
};

export const useWhatsappInbox = (page = 1, phoneNumberId) =>
  useQuery({
    queryKey: ["whatsapp-inbox", page, phoneNumberId || "all"],
    queryFn: () => fetchWhatsappInbox({ page, phoneNumberId }),
    staleTime: 15 * 1000,
    keepPreviousData: true,
  });

const fetchWhatsappThread = async (conversationId) => {
  const { data } = await axios.get(`/whatsapp-admin/conversations/${conversationId}`);
  return data;
};

export const useWhatsappThread = (conversationId) =>
  useQuery({
    queryKey: ["whatsapp-thread", conversationId],
    queryFn: () => fetchWhatsappThread(conversationId),
    enabled: Boolean(conversationId),
    staleTime: 10 * 1000,
  });

// ---------------------------------------------------------------------------
// Opted-out — who's currently blocked from receiving template sends, and re-consent
// ---------------------------------------------------------------------------

const fetchOptedOut = async () => {
  const { data } = await axios.get("/whatsapp-admin/opted-out");
  return data;
};

const clearOptOut = async (conversationId) => {
  const { data } = await axios.post(`/whatsapp-admin/opted-out/${conversationId}/clear`);
  return data;
};

export const useOptedOutConversations = () =>
  useQuery({
    queryKey: ["whatsapp-opted-out"],
    queryFn: fetchOptedOut,
    staleTime: 30 * 1000,
  });

export const useClearOptOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearOptOut,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["whatsapp-opted-out"] }),
  });
};
